const COMBINING_MARKS = /[\u0300-\u036f]/g;
const NON_ASCII = /[\u0080-\uffff]/;
// Dropped rather than treated as spaces, so "obrien" finds O'Brien and "jr" finds J.R.
const APOSTROPHES_AND_PERIODS = /['\u2018\u2019\u02bc`.]/g;
// Dropped from names, so "vargasortiz" finds Vargas-Ortiz, but split search words, so "smith-jones" finds Jones Smith.
const DASHES = /[-\u2010-\u2014]+/g;
// Spaces and commas separate search words, as in the portal's search.
const WORD_SEPARATORS = /[\s,]+/;
// myNHA stores names as code page 1252 varchar, so this Latin range covers every letter a name can contain.
const LETTER_OR_DIGIT = /[0-9a-z\u00c0-\u00d6\u00d8-\u00f6\u00f8-\u024f]/i;

// Folding is pure, so remembering results can't change them. It saves re-folding the same names on every keystroke,
// and some lists re-filter on every change detection. Cleared when full to keep memory bounded.
const FOLDED_CACHE_LIMIT = 10000;
const foldedCache = new Map<string, string>();

function lowerText(value: unknown): string {
	return value == null ? '' : String(value).toLowerCase();
}

/** Lowercases text and strips accents, apostrophes, hyphens and periods so searches can compare plain letters. */
export function foldSearchText(value: unknown): string {
	const text = lowerText(value);
	let folded = foldedCache.get(text);

	if (folded === undefined) {
		folded = text;

		// Most names are plain ASCII; skipping the Unicode work keeps filtering long lists fast.
		if (NON_ASCII.test(folded)) {
			// ø has no Unicode decomposition, but the portal's accent-insensitive collation treats it as o.
			folded = folded.normalize('NFD').replace(COMBINING_MARKS, '').replace(/ø/g, 'o');
		}

		folded = folded.replace(APOSTROPHES_AND_PERIODS, '').replace(DASHES, '');

		if (foldedCache.size >= FOLDED_CACHE_LIMIT) {
			foldedCache.clear();
		}
		foldedCache.set(text, folded);
	}

	return folded;
}

/** A record's fields prepared for searching. Build it once per record and reuse it for every search. */
export interface SearchableText {
	folded: string;
	asTyped: string;
}

export function toSearchableText(fields: ReadonlyArray<string | number | null | undefined>): SearchableText {
	return { folded: fields.map(foldSearchText).join(' '), asTyped: fields.map(lowerText).join(' ') };
}

interface ParsedSearch {
	words: string[];
	// Made only of symbols, so compared exactly as typed, as in the portal: "-" finds hyphenated names.
	symbols: string[];
}

// Parsing is pure as well, and callers that test one row at a time (nameSearch) pass the same search for every row.
// Keyed by the exact search value, so a blank or undefined search is just another key.
const PARSED_CACHE_LIMIT = 100;
const parsedCache = new Map<unknown, ParsedSearch>();

function parseSearch(searchText: unknown): ParsedSearch {
	let parsed = parsedCache.get(searchText);

	if (parsed === undefined) {
		parsed = { words: [], symbols: [] };

		for (const word of lowerText(searchText).split(WORD_SEPARATORS).filter(Boolean)) {
			if (LETTER_OR_DIGIT.test(word)) {
				parsed.words.push(...word.split(DASHES).map(foldSearchText).filter(Boolean));
			} else {
				parsed.symbols.push(word);
			}
		}

		if (parsedCache.size >= PARSED_CACHE_LIMIT) {
			parsedCache.clear();
		}
		parsedCache.set(searchText, parsed);
	}

	return parsed;
}

function includesAll(text: string, terms: string[]): boolean {
	return terms.every((term) => text.includes(term));
}

/**
 * Returns a test for prepared records. Every word of the search must appear somewhere in the record, in any order,
 * ignoring case, accents, apostrophes, hyphens and periods; a word made only of symbols must appear exactly as typed.
 * A blank search matches everything.
 */
export function createSearchMatcher(searchText: string): (record: SearchableText) => boolean {
	const { words, symbols } = parseSearch(searchText);

	return ({ folded, asTyped }) => includesAll(folded, words) && includesAll(asTyped, symbols);
}

/** The same test for one unprepared record. To filter a list, prefer createSearchMatcher with records prepared once. */
export function matchesSearchTerms(fields: ReadonlyArray<string | number | null | undefined>, searchText: string): boolean {
	const { words, symbols } = parseSearch(searchText);

	// Builds only the text each kind of word needs; most searches have no symbol words.
	return (
		(words.length === 0 || includesAll(fields.map(foldSearchText).join(' '), words)) &&
		(symbols.length === 0 || includesAll(fields.map(lowerText).join(' '), symbols))
	);
}
